import React from 'react';
import classNames from 'classnames';

import Field from '../../Field';

import css from './SectionContainer.module.css';
import SearchBar from './SearchBar/SearchBar';
import { ListingCard } from '../../../../components';
import Slider from 'react-slick';
import BrandIconCard from '../../../../components/BrandIconCard/BrandIconCard';
import { useHistory } from 'react-router-dom';
import { createResourceLocatorString } from '../../../../util/routes';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { FormattedMessage } from 'react-intl';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This component can be used to wrap some common styles and features of Section-level components.
 * E.g: const SectionHero = props => (<SectionContainer><H1>Hello World!</H1></SectionContainer>);
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.id id of the section
 * @param {string?} props.as tag/element name. Defaults to 'section'.
 * @param {ReactNode} props.children
 * @param {Object} props.appearance
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} containing wrapper that can be used inside Block components.
 */


function SampleNextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style }}
      onClick={onClick}
    >
      <BrandIconCard type='slickright' />
    </div>
  );
}


function SamplePrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, }}
      onClick={onClick}
    >
      <BrandIconCard type="slickleft" />
    </div>
  );
}
const SectionContainer = props => {
  const { className, rootClassName, id, as, children, appearance, options, ...otherProps } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const Tag = as || 'section';
  const classes = classNames(rootClassName || css.root, className);
  const { listings = [], hostListings=[] } = options || {};
  const featuredExp = listings.filter((listing) => listing?.attributes?.publicData?.isFeaturedExperiences)

  const handleSubmit = values => {
    const searchParams = {
      keywords: values?.keywords,
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };

  const goToSearchPage = () => {
    history.push('/s');
  };

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3.2,
    slidesToScroll: 3,
    initialSlide: 0,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      {
        breakpoint: 1220,
        settings: {
          slidesToShow: 3.1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1120,
        settings: {
          slidesToShow: 2.9,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 920,
        settings: {
          slidesToShow: 2.5,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2.1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1.5,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 500,
        settings: {
          slidesToShow: 1.1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  return (
    <Tag className={classes} id={id} {...otherProps}>
      {appearance?.fieldType === 'customAppearance' ? (
        <Field
          data={{ alt: `Background image for ${id}`, ...appearance }}
          className={className}
          options={options}
        />
      ) : null}

      <div
        className={
          id === 'hero-section'
            ? css.heroContent
            : id === 'how-vivw-works'
            ? css.workContent
            : id === 'featured-hosts'
            ? css.featureContent
            : id === 'host-hero'
            ? css.hostHeroContent
            : id === 'why-host'
            ? css.whyHostContent
            : id === 'footer'
            ? css.footerContent
            : id === 'featured-experiences'
            ? css.featuredContent
            : id === 'elevated-experiences'
            ? css.elevatedContent
            : id === 'host-hub-home'
            ? css.hostHubContent
            : id === 'feature-host-slider'
            ? css.featuredSlider
            : css.sectionContent
        }
      >
        <>
          {id === 'featured-experiences' ? (
            <div className={css.featuresListing}>
              <div className={css.featureHeading}>
                <FormattedMessage id="landingPage.sectionContainer.featureExperiences.heading" />
              </div>
              <Slider {...settings} className={css.homeSlick}>
                {Array.isArray(listings) &&
                  listings?.map((item,i) => {
                    return <ListingCard key={i} listing={item} featureExp={true} showAuthorInfo={false} />;
                  })}
              </Slider>
              <div className={css.viewAllButton}>
                <button onClick={goToSearchPage}>
                  <FormattedMessage id="landingPage.sectionContainer.allExperiences.button" />
                </button>
                {/* <NamedLink name="SearchPage">View all Experiences</NamedLink> */}
              </div>
            </div>
          ) : id === 'feature-host-slider' ? (
            <div className={css.featuresListing}>
              <div className={css.featureHeading}>
                <FormattedMessage id="landingPage.sectionContainer.featurehost.heading" />
              </div>
              <Slider {...settings} className={css.homeSlick}>
                {Array.isArray(hostListings) &&
                  hostListings?.map((item, i) => {
                    return <ListingCard key={i} isFeatured={true} listing={item} featureHost={true} showAuthorInfo={false} />;
                  })}
              </Slider>
            </div>
          ) : (
            children
          )}
          {id === 'hero-section' ? (
            <div className={css.searchContent}>
              <SearchBar onSubmit={handleSubmit} />
            </div>
          ) : null}
        </>
      </div>
    </Tag>
  );
};

export default SectionContainer;
